import type { ErrorWithDigest } from "@/modules/server/createInternalErrorFromNextRequest"
import type { IncomingMessage, ServerResponse } from "http"
import type { FunctionComponent } from "react"

import { useEffect } from "react"

import { TranslationProvider } from "@/modules/i18n/components/TranslationProvider"
import { StandardErrorWithLazyTitle } from "@/modules/layouts/components/errors/StandardError/StandardErrorWithLazyTitle"
import { logger } from "@/modules/monitoring/logger"
import { getErrorIdFromHeaders } from "@/modules/server/getErrorIdFromHeaders"
import { AppTrackingPageViewPropsUpdater } from "@/modules/tracking/components/AppTrackingPageViewPropsUpdater"

type ErrorProps = {
  message: string
  digest: string
}

type GetInitialPropsParams = {
  req: IncomingMessage
  res: ServerResponse
  err: ErrorWithDigest
}

export type ErrorComponent = FunctionComponent<ErrorProps> & {
  getInitialProps: (params: GetInitialPropsParams) => ErrorProps
}

export const Error: ErrorComponent = ({ message, digest }: ErrorProps) => {
  useEffect(() => {
    // Client log to TrackJS
    logger.error(`${message} - ${digest}`)
  }, [message, digest])

  return (
    <AppTrackingPageViewPropsUpdater>
      <TranslationProvider>
        <StandardErrorWithLazyTitle errorId={digest || ""} />
      </TranslationProvider>
    </AppTrackingPageViewPropsUpdater>
  )
}

Error.getInitialProps = ({ req, res, err }: GetInitialPropsParams): ErrorProps => {
  let { digest } = err

  // Force the injection of a traceable digest if there isn't a JT one already provided
  // (a JT traceable digest looks like "rayId // reqId")
  if (!digest || !/ \/\/ /.test(digest)) {
    digest = getErrorIdFromHeaders(req.headers)
  }

  // Server log to Loki
  logger.error({ digest, statusCode: res.statusCode }, err.message)
  return { digest, message: err.message }
}

export default Error
